import { useState, type ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ErrorText } from '@/components/shared/typography/ErrorText';
import {
  Severity,
  severityOptions,
} from '@kirin/api-client/contracts/shared/severity';
import type {
  CatalogCategory,
  NewCustomCategory,
  EditBuiltinCategory,
} from '@kirin/api-client/contracts/ai-instruction-catalog';

export type CategoryFormSubmit =
  | { kind: 'custom'; data: NewCustomCategory }
  | { kind: 'builtin'; data: EditBuiltinCategory };

interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Category being edited; null = create a new custom category. */
  category: CatalogCategory | null;
  onSubmit: (payload: CategoryFormSubmit) => Promise<void>;
  isPending: boolean;
}

/** Keep in sync with `categoryKeywordsField.max(50)` in the api-client contract. */
const MAX_KEYWORDS = 50;

function splitKeywords(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const k of raw.split(',')) {
    const trimmed = k.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue; // dedupe (case-insensitive) → no duplicate keys
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

interface CategoryFormProps {
  category: CatalogCategory | null;
  onSubmit: (payload: CategoryFormSubmit) => Promise<void>;
  onCancel: () => void;
  isPending: boolean;
}

/**
 * Inner form. State is initialized from `category` via useState initializers
 * (no effect) — the parent remounts it with a fresh `key` per category, so the
 * fields always reflect the current target without a setState-in-effect.
 */
function CategoryForm({
  category,
  onSubmit,
  onCancel,
  isPending,
}: CategoryFormProps): ReactElement {
  const isBuiltIn = category?.isBuiltIn ?? false;
  const isEdit = category !== null;

  const [name, setName] = useState(category?.name ?? '');
  const [severity, setSeverity] = useState<Severity>(
    (category?.severity as Severity) ?? Severity.High
  );
  const [reference, setReference] = useState(category?.reference ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  const [keywords, setKeywords] = useState(category?.keywords.join(', ') ?? '');
  const [instruction, setInstruction] = useState(category?.instruction ?? '');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    const parsedKeywords = splitKeywords(keywords);

    // Required fields (reference is optional): name, severity, why it matters,
    // at least one keyword, example instruction. Name/why are locked for
    // built-ins (always present), so they never appear as missing there.
    const missing: string[] = [];
    if (!isBuiltIn && !name.trim()) missing.push('category name');
    if (!isBuiltIn && !description.trim()) missing.push('why this matters');
    if (parsedKeywords.length === 0) {
      missing.push('at least one detection keyword');
    }
    if (!instruction.trim()) missing.push('example instruction');
    if (missing.length > 0) {
      setError(`Please fill: ${missing.join(', ')}.`);
      return;
    }
    // Surface the contract's keyword cap as inline validation rather than
    // letting parseSchema throw a raw Zod message on save.
    if (parsedKeywords.length > MAX_KEYWORDS) {
      setError(
        `Too many detection keywords (${parsedKeywords.length}). Use at most ${MAX_KEYWORDS}.`
      );
      return;
    }

    try {
      if (isBuiltIn) {
        await onSubmit({
          kind: 'builtin',
          data: {
            severity,
            keywords: parsedKeywords,
            instruction: instruction.trim(),
          },
        });
      } else {
        await onSubmit({
          kind: 'custom',
          data: {
            name: name.trim(),
            severity,
            reference: reference.trim() || undefined,
            description: description.trim(),
            keywords: parsedKeywords,
            instruction: instruction.trim(),
          },
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save category.');
    }
  };

  return (
    <>
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="cat-name">Category name *</Label>
          <Input
            id="cat-name"
            placeholder="e.g. Internal API gateway usage"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={isBuiltIn}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="cat-severity">Severity *</Label>
          <Select
            value={severity}
            onValueChange={v => setSeverity(v as Severity)}
          >
            <SelectTrigger id="cat-severity">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              {severityOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="cat-reference">Reference</Label>
          <Input
            id="cat-reference"
            placeholder="Optional, e.g. INTERNAL-06"
            value={reference}
            onChange={e => setReference(e.target.value)}
            disabled={isBuiltIn}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="cat-description">
            Why this matters for AI agents *
          </Label>
          <Textarea
            id="cat-description"
            placeholder="Short description of the risk for AI-generated code."
            value={description}
            onChange={e => setDescription(e.target.value)}
            disabled={isBuiltIn}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="cat-keywords">Detection keywords *</Label>
          <Textarea
            id="cat-keywords"
            placeholder="comma, separated, keywords"
            value={keywords}
            onChange={e => setKeywords(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="cat-instruction">Example instruction *</Label>
          <Textarea
            id="cat-instruction"
            placeholder="The instruction to add to CLAUDE.md or .cursorrules."
            value={instruction}
            onChange={e => setInstruction(e.target.value)}
          />
        </div>

        {error && <ErrorText as="p">{error}</ErrorText>}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={() => void handleSubmit()} disabled={isPending}>
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save category'}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * KIR-3515: create / edit dialog for catalog categories.
 *
 * - Create (category=null) and edit-custom: all fields editable.
 * - Edit built-in (category.isBuiltIn): name, reference, and "why it matters"
 *   are locked; only severity, detection keywords, and example instruction can
 *   change. Save is blocked until required fields are filled, naming which are
 *   missing (mirrors the mockup's inline message).
 */
export function CategoryFormDialog({
  open,
  onOpenChange,
  category,
  onSubmit,
  isPending,
}: CategoryFormDialogProps): ReactElement {
  const isBuiltIn = category?.isBuiltIn ?? false;
  const title = !category
    ? 'Add custom category'
    : isBuiltIn
      ? `Edit ${category.name}`
      : 'Edit custom category';

  const handleSubmit = async (payload: CategoryFormSubmit): Promise<void> => {
    await onSubmit(payload);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isBuiltIn
              ? 'Name and reference are fixed for framework categories. You can change severity, keywords, and the example instruction.'
              : 'Define a proprietary category for your AI instruction catalog. Reference is optional.'}
          </DialogDescription>
        </DialogHeader>

        {/* Keyed so switching target category re-initializes the form fields. */}
        <CategoryForm
          key={category?.id ?? 'new'}
          category={category}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          isPending={isPending}
        />
      </DialogContent>
    </Dialog>
  );
}
